import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../core/utils/api";
import { pushDevDebugEvent } from "../../core/utils/devDebugBuffer";

export const MetadataContext = createContext();

export const useMetadata = () => {
  const context = useContext(MetadataContext);
  if (!context) {
    throw new Error("useMetadata must be used within a MetadataProvider");
  }
  return context;
};

export const MetadataProvider = ({ children }) => {
  const METADATA_ENDPOINTS = useMemo(
    () => [
      { key: "baseStyle", url: "/api/metadata/base-style/" },
      { key: "characterStyle", url: "/api/metadata/character-style/" },
      { key: "imageStyle", url: "/api/metadata/image-style/" },
      { key: "containerStyle", url: "/api/metadata/container-style/" },
      { key: "pattern", url: "/api/metadata/pattern/" },
      { key: "background", url: "/api/metadata/background/" },
      { key: "margin", url: "/api/metadata/margin/" },
      { key: "padding", url: "/api/metadata/padding/" },
      { key: "component", url: "/api/metadata/component/" },
      { key: "hero", url: "/api/metadata/hero/" },
      { key: "title", url: "/api/metadata/title/" },
      { key: "text", url: "/api/metadata/text/" },
      { key: "video", url: "/api/metadata/video/" },
      { key: "button", url: "/api/metadata/button/" },
      { key: "image", url: "/api/metadata/image/" },
      { key: "imageCollection", url: "/api/metadata/image-collection/" },
      { key: "page", url: "/api/metadata/page/" },
      { key: "buttonCollection", url: "/api/metadata/button-collection/" },
      { key: "file", url: "/api/metadata/file/" },
      // Navigation metadata
      { key: "navbar", url: "/api/metadata/navbar/" },
      { key: "navbarSection", url: "/api/metadata/navbar-section/" },
      { key: "sidebar", url: "/api/metadata/sidebar/" },
      { key: "sidebarSection", url: "/api/metadata/sidebar-section/" },
      { key: "footer", url: "/api/metadata/footer/" },
      // Hero layout presets (not processed like other metadata)
      { key: "heroLayoutPresets", url: "/api/metadata/hero-layout-presets/" },
      // Hero automatic mode definitions (not processed like other metadata)
      { key: "heroModes", url: "/api/metadata/hero-modes/" },
      // Component automatic mode definitions (not processed like other metadata)
      { key: "componentModes", url: "/api/metadata/component-modes/" },
    ],
    [],
  );

  const endpointsByKey = useMemo(() => {
    const map = new Map();
    METADATA_ENDPOINTS.forEach((e) => map.set(e.key, e.url));
    return map;
  }, [METADATA_ENDPOINTS]);

  // Extract MAIN fields and remove the MAIN group from the structure
  const processMetadata = useCallback((rawMetadata) => {
    if (!rawMetadata) return null;

    const mainFields = {};
    const otherGroups = {};

    Object.entries(rawMetadata).forEach(([groupName, groupData]) => {
      if (groupName === "main") {
        Object.entries(groupData?.fields || {}).forEach(([fieldName, fieldData]) => {
          mainFields[fieldName] = fieldData;
        });
      } else {
        otherGroups[groupName] = groupData;
      }
    });

    return {
      main_fields: mainFields,
      ...otherGroups,
    };
  }, []);

  // Transform hero layout presets from snake_case to camelCase
  const transformPresets = useCallback((presetsData) => {
    if (!presetsData || !presetsData.presets) return null;

    return {
      ...presetsData,
      presets: presetsData.presets.map((preset) => ({
        ...preset,
        columnSequences: preset.column_sequences,
        column_sequences: undefined,
      })),
    };
  }, []);

  const fetchJsonWithTimeout = useCallback(async (url, { timeoutMs = 12000 } = {}) => {
    const ctrl = new AbortController();
    const t = window.setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      // suppressUnauthorizedEvent: metadata endpoints fire on app mount before the
      // user may be authenticated (e.g. during the Google OAuth callback page load).
      // Any 401s here must NOT trigger the global user-auth logout event.
      const res = await apiFetch(url, {
        credentials: "include",
        signal: ctrl.signal,
        suppressUnauthorizedEvent: true,
      });
      if (!res.ok) {
        throw new Error(`${url} ${res.status}`);
      }
      return await res.json();
    } finally {
      window.clearTimeout(t);
    }
  }, []);

  // Metadata now includes rich group information with titles, descriptions, icons, etc.
  // Structure: { groupName: { group_info: {...}, fields: {...} } }
  // The MAIN group (order=0) should appear at the top and be handled specially
  // MAIN fields are extracted separately and don't appear as a group
  const [metadata, setMetadata] = useState({
    baseStyle: null,
    characterStyle: null,
    imageStyle: null,
    containerStyle: null,
    pattern: null,
    background: null,
    margin: null,
    padding: null,
    component: null,
    hero: null,
    title: null,
    text: null,
    video: null,
    button: null,
    image: null,
    file: null,
    imageCollection: null,
    page: null,
    // Navigation metadata
    navbar: null,
    navbarSection: null,
    sidebar: null,
    sidebarSection: null,
    footer: null,
    // Hero layout presets (backend-driven)
    heroLayoutPresets: null,
    // Hero automatic mode definitions (backend-driven)
    heroModes: null,
    // Component automatic mode definitions (backend-driven)
    componentModes: null,
    loading: true,
    error: null,
  });

  // Deduplicate per-key refetches so aggressive retry loops don't flood the network.
  const inflightRef = useRef(new Map()); // key -> Promise<{ ok, key, value?, reason? }>

  const refetchMetadataKeys = useCallback(
    async (keys, { reason = "manual", timeoutMs = 12000 } = {}) => {
      const requested = Array.from(new Set((keys || []).filter(Boolean)));
      if (requested.length === 0) return [];

      pushDevDebugEvent({
        type: "metadata-refetch:start",
        keys: requested,
        reason,
      });

      const tasks = requested.map(async (key) => {
        if (!endpointsByKey.has(key)) {
          return { ok: false, key, reason: `Unknown metadata key: ${key}` };
        }

        if (inflightRef.current.has(key)) {
          return inflightRef.current.get(key);
        }

        const url = endpointsByKey.get(key);
        const p = (async () => {
          try {
            const json = await fetchJsonWithTimeout(url, { timeoutMs });
            const value =
              key === "heroLayoutPresets"
                ? transformPresets(json)
                : key === "heroModes" || key === "componentModes"
                  ? json
                  : processMetadata(json);
            return { ok: true, key, value };
          } catch (err) {
            const msg = err?.name === "AbortError" ? "Request timed out" : String(err?.message || err);
            return { ok: false, key, reason: msg };
          } finally {
            inflightRef.current.delete(key);
          }
        })();

        inflightRef.current.set(key, p);
        return p;
      });

      const results = await Promise.all(tasks);

      setMetadata((prev) => {
        const next = { ...prev };

        // Apply successes
        results.forEach((r) => {
          if (r.ok) {
            next[r.key] = r.value;
          }
        });

        // Merge failures into error list (keep unrelated prior failures)
        const prevFailures = Array.isArray(prev.error)
          ? prev.error.filter((f) => f && !requested.includes(f.key))
          : [];
        const nextFailures = results
          .filter((r) => !r.ok)
          .map((r) => ({ key: r.key, reason: r.reason, retryReason: reason }));

        const combined = [...prevFailures, ...nextFailures];
        next.error = combined.length > 0 ? combined : null;

        return next;
      });

      pushDevDebugEvent({
        type: "metadata-refetch:done",
        keys: requested,
        reason,
        results: results.map((r) => ({ key: r.key, ok: r.ok, reason: r.ok ? undefined : r.reason })),
      });

      return results;
    },
    [endpointsByKey, fetchJsonWithTimeout, processMetadata, transformPresets],
  );

  useEffect(() => {
    const fetchMetadata = async () => {
      console.log("[MetadataContext] Starting to fetch metadata...");
      pushDevDebugEvent({ type: "metadata-initial-fetch:start" });

      try {
        const results = await Promise.allSettled(
          METADATA_ENDPOINTS.map((e) =>
            fetchJsonWithTimeout(e.url).catch((err) =>
              Promise.reject(`${e.key} ${String(err?.message || err)}`),
            ),
          ),
        );

        const raw = {};
        const failures = [];
        results.forEach((res, idx) => {
          const { key } = METADATA_ENDPOINTS[idx];
          if (res.status === "fulfilled") {
            raw[key] = res.value;
          } else {
            raw[key] = null;
            failures.push({ key, reason: res.reason });
          }
        });

        setMetadata((prev) => ({
          ...prev,
          baseStyle: processMetadata(raw.baseStyle),
          characterStyle: processMetadata(raw.characterStyle),
          imageStyle: processMetadata(raw.imageStyle),
          containerStyle: processMetadata(raw.containerStyle),
          pattern: processMetadata(raw.pattern),
          background: processMetadata(raw.background),
          margin: processMetadata(raw.margin),
          padding: processMetadata(raw.padding),
          component: processMetadata(raw.component),
          hero: processMetadata(raw.hero),
          title: processMetadata(raw.title),
          text: processMetadata(raw.text),
          video: processMetadata(raw.video),
          button: processMetadata(raw.button),
          image: processMetadata(raw.image),
          imageCollection: processMetadata(raw.imageCollection),
          page: processMetadata(raw.page),
          buttonCollection: processMetadata(raw.buttonCollection),
          file: processMetadata(raw.file),
          navbar: processMetadata(raw.navbar),
          navbarSection: processMetadata(raw.navbarSection),
          sidebar: processMetadata(raw.sidebar),
          sidebarSection: processMetadata(raw.sidebarSection),
          footer: processMetadata(raw.footer),
          // Hero layout presets - transform from snake_case to camelCase
          heroLayoutPresets: transformPresets(raw.heroLayoutPresets),
          // Hero automatic mode definitions - pass through raw
          heroModes: raw.heroModes || null,
          // Component automatic mode definitions - pass through raw
          componentModes: raw.componentModes || null,
          loading: false,
          error: failures.length > 0 ? failures : null,
        }));
        console.log("[MetadataContext] Metadata loaded (partial OK). Failures:", failures);
        pushDevDebugEvent({
          type: "metadata-initial-fetch:done",
          failures: failures.map((f) => ({ key: f.key, reason: String(f.reason) })),
        });
      } catch (error) {
        console.error("[MetadataContext] Unexpected failure loading metadata:", error);
        setMetadata((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load metadata",
        }));
        pushDevDebugEvent({
          type: "metadata-initial-fetch:error",
          error: String(error?.message || error),
        });
      }
    };

    fetchMetadata();
  }, []);

  // Auto-retry any keys that failed during the initial fetch.
  // Runs once after loading finishes; a single retry with a short delay handles
  // transient network hiccups (the most common reason presets fail to load).
  const autoRetryDoneRef = useRef(false);

  useEffect(() => {
    if (metadata.loading) return; // still in initial fetch
    if (autoRetryDoneRef.current) return; // already scheduled
    autoRetryDoneRef.current = true;

    if (!Array.isArray(metadata.error) || metadata.error.length === 0) return;

    const failedKeys = metadata.error
      .filter((f) => f && f.key)
      .map((f) => f.key);
    if (failedKeys.length === 0) return;

    const timer = setTimeout(() => {
      console.log("[MetadataContext] Auto-retrying failed metadata keys:", failedKeys);
      refetchMetadataKeys(failedKeys, { reason: "auto-retry:initial-failures" });
    }, 2000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata.loading, metadata.error, refetchMetadataKeys]);

  const loadedKeysSignature = useMemo(() => {
    const keys = Object.keys(metadata || {})
      .filter((k) => !["loading", "error"].includes(k))
      .filter((k) => metadata?.[k] != null)
      .sort();
    return keys.join("|");
  }, [
    metadata.baseStyle,
    metadata.characterStyle,
    metadata.imageStyle,
    metadata.containerStyle,
    metadata.pattern,
    metadata.background,
    metadata.margin,
    metadata.padding,
    metadata.component,
    metadata.hero,
    metadata.title,
    metadata.text,
    metadata.video,
    metadata.button,
    metadata.image,
    metadata.file,
    metadata.imageCollection,
    metadata.page,
    metadata.buttonCollection,
    metadata.navbar,
    metadata.navbarSection,
    metadata.sidebar,
    metadata.sidebarSection,
    metadata.footer,
    metadata.heroLayoutPresets,
    metadata.heroModes,
    metadata.componentModes,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (typeof window === "undefined") return;
    try {
      const snapshot = {
        ts: Date.now(),
        loading: !!metadata.loading,
        error: metadata.error,
        loadedKeys: loadedKeysSignature ? loadedKeysSignature.split("|") : [],
      };
      window.__VITAE_METADATA_STATE__ = snapshot;
      pushDevDebugEvent({ type: "metadata-snapshot", snapshot });
    } catch (_) {}
  }, [metadata.loading, metadata.error, loadedKeysSignature]);

  // Memoize the context value to prevent cascade re-renders
  // Only recreate when individual metadata properties actually change
  // Note: We depend on individual properties, not the metadata object itself,
  // to prevent unnecessary re-renders when the object reference changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => ({ ...metadata, refetchMetadataKeys }), [
    metadata.baseStyle,
    metadata.characterStyle,
    metadata.imageStyle,
    metadata.containerStyle,
    metadata.pattern,
    metadata.background,
    metadata.margin,
    metadata.padding,
    metadata.component,
    metadata.hero,
    metadata.title,
    metadata.text,
    metadata.video,
    metadata.button,
    metadata.image,
    metadata.file,
    metadata.imageCollection,
    metadata.page,
    metadata.buttonCollection,
    metadata.navbar,
    metadata.navbarSection,
    metadata.sidebar,
    metadata.sidebarSection,
    metadata.footer,
    metadata.heroLayoutPresets,
    metadata.heroModes,
    metadata.componentModes,
    metadata.loading,
    metadata.error,
    refetchMetadataKeys,
  ]);

  return (
    <MetadataContext.Provider value={value}>
      {children}
    </MetadataContext.Provider>
  );
};
